/**
 * 錢包連接 Store
 * 使用 Pinia 管理錢包連接狀態
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { UniversalConnector } from '@reown/appkit-universal-connector'
import { getUniversalConnector, getAppKit } from '../config/walletconnect'
import type { AppKit } from '@reown/appkit'
import { env } from '../config/env'
import { logger } from '../utils/logger'

export interface WalletSession {
  accounts: string[]
  chainId: number
  namespace: string
}

export const useWalletStore = defineStore('wallet', () => {
  // 狀態
  const connector = ref<UniversalConnector | null>(null)
  const appKit = ref<AppKit | null>(null) // AppKit 實例（用於直接連接特定錢包）
  const session = ref<WalletSession | null>(null)
  const isConnecting = ref(false)
  const error = ref<string | null>(null)
  const connectionType = ref<'walletconnect' | 'direct' | null>(null) // 追蹤連接類型

  // 計算屬性
  const isConnected = computed(() => {
    return session.value !== null && session.value.accounts.length > 0
  })

  const address = computed(() => {
    return session.value?.accounts[0] || null
  })

  const chainId = computed(() => {
    return session.value?.chainId || null
  })

  // 初始化 AppKit（使用 AppKit Core API）
  async function initAppKit() {
    try {
      if (!appKit.value && typeof window !== 'undefined') {
        appKit.value = getAppKit()
        
        // 等待 AppKit 準備就緒
        await appKit.value.ready()
        
        // 監聽連接狀態變化
        const account = appKit.value.getAccount('eip155')
        if (account?.address) {
          // 已經有連接，更新會話
          const caipAddress = appKit.value.getCaipAddress('eip155')
          const chainId = appKit.value.getChainId()
          
          session.value = {
            accounts: [account.address],
            chainId: chainId ? parseInt(chainId.toString()) : 1,
            namespace: 'eip155'
          }
          connectionType.value = 'walletconnect'
        }
      }
      return appKit.value
    } catch (err: any) {
      error.value = err.message || '初始化 AppKit 失敗'
      logger.error('初始化 AppKit 失敗:', err)
      throw err
    }
  }

  // 初始化連接器（備用方案）
  async function initConnector() {
    try {
      if (!connector.value) {
        connector.value = await getUniversalConnector()
        
        // 監聽會話變化
        if (connector.value.provider) {
          // 監聽會話更新（當用戶掃描 QR 碼後連接完成）
          connector.value.provider.on('session_update', async (sessionData: any) => {
            // 會話已更新
            if (sessionData && sessionData.namespaces) {
              updateSession(sessionData)
              // 連接完成，重置連接狀態
              isConnecting.value = false
          // 檢查並切換到目標網絡
          try {
            await ensureTargetNetworkConnection()
          } catch (err) {
            logger.warn('切換到目標網絡失敗，但連接已成功')
          }
            }
          })

          // 監聽連接事件（備用，當連接建立時）
          connector.value.provider.on('connect', async () => {
            // WalletConnect 已連接
            isConnecting.value = false
            // 檢查是否有會話
            if (connector.value?.provider?.session) {
              updateSession(connector.value.provider.session)
          // 檢查並切換到目標網絡
          try {
            await ensureTargetNetworkConnection()
          } catch (err) {
            logger.warn('切換到目標網絡失敗，但連接已成功')
          }
            }
          })

          // 監聽會話刪除
          connector.value.provider.on('session_delete', () => {
            // 會話已刪除
            disconnect()
          })

          // 監聽賬戶變化
          connector.value.provider.on('accountsChanged', (accounts: string[]) => {
            // 賬戶已變化
            if (session.value) {
              session.value.accounts = accounts
            }
          })

          // 監聽鏈變化
          connector.value.provider.on('chainChanged', async (chainId: string) => {
            // 鏈已變化
            const newChainId = parseInt(chainId, 16)
            if (session.value) {
              session.value.chainId = newChainId
            }
            // 如果切換到非目標鏈，提示用戶
            if (!isTargetChain(newChainId)) {
              logger.warn(`檢測到切換到非目標鏈`)
              // 可以選擇自動切換或提示用戶
              // await ensureTargetNetworkConnection()
            }
          })

          // 檢查現有會話
          const existingSession = connector.value.provider.session
          if (existingSession) {
            // 發現現有會話
            updateSession(existingSession)
          }
        }
      }
      return connector.value
    } catch (err: any) {
      error.value = err.message || '初始化連接器失敗'
      logger.error('初始化連接器失敗:', err)
      throw err
    }
  }

  // 通過錢包 ID 直接連接（使用 AppKit Core API）
  async function connectWalletById(walletId: string) {
    try {
      isConnecting.value = true
      error.value = null

      // 初始化 AppKit
      if (!appKit.value) {
        await initAppKit()
      }

      if (!appKit.value) {
        throw new Error('AppKit 未初始化')
      }

      // 使用 Universal Provider 直接連接特定錢包
      try {
        const universalProvider = await appKit.value.getUniversalProvider()
        if (universalProvider) {
          // 方法 1: 嘗試通過 Universal Provider 的 pair 方法連接
          // 需要獲取錢包的 deep link URI
          const provider = universalProvider as any
          
          // 嘗試通過 provider 的 client 來連接特定錢包
          if (provider.client) {
            const client = provider.client as any
            
            // 嘗試使用 client 的 connect 方法，傳入錢包 ID
            // 注意：這可能需要錢包的 deep link 或 QR code URI
            if (client.connect && typeof client.connect === 'function') {
              try {
                // 嘗試連接特定錢包
                // 注意：WalletConnect 可能需要通過 deep link 來連接
                // 對於桌面錢包，可能需要使用錢包的 deep link URI
                const result = await client.connect({
                  requiredNamespaces: {
                    eip155: {
                      methods: [
                        'eth_sendTransaction',
                        'eth_signTransaction',
                        'eth_sign',
                        'personal_sign',
                        'eth_signTypedData',
                        'eth_signTypedData_v4',
                        'wallet_switchEthereumChain',
                        'wallet_addEthereumChain'
                      ],
                      chains: ['eip155:1', 'eip155:11155111'],
                      events: ['chainChanged', 'accountsChanged']
                    }
                  },
                  // 嘗試指定錢包 ID（如果支持）
                  walletId
                })
                
                if (result) {
                  // 等待會話建立
                  await new Promise(resolve => setTimeout(resolve, 1000))
                  
                  const account = appKit.value.getAccount('eip155')
                  if (account?.address) {
                    const chainId = appKit.value.getChainId()
                    session.value = {
                      accounts: [account.address],
                      chainId: chainId ? parseInt(chainId.toString()) : 1,
                      namespace: 'eip155'
                    }
                    connectionType.value = 'walletconnect'
                    return
                  }
                }
              } catch (err) {
                // 連接失敗，嘗試其他方法
              }
            }
          }
          
          // 方法 2: 使用 AppKit 的 open 方法，但這會顯示選擇器
          // 這是備用方案，因為直接指定 walletId 可能不被支持
          // 無法直接連接特定錢包，將顯示選擇器
          await appKit.value.open({ view: 'Connect' })
          
          // 監聽連接完成
          const checkConnection = () => {
            const account = appKit.value?.getAccount('eip155')
            if (account?.address) {
              const chainId = appKit.value?.getChainId()
              session.value = {
                accounts: [account.address],
                chainId: chainId ? parseInt(chainId.toString()) : 1,
                namespace: 'eip155'
              }
              connectionType.value = 'walletconnect'
              return true
            }
            return false
          }

          // 等待連接（最多 10 秒）
          for (let i = 0; i < 20; i++) {
            await new Promise(resolve => setTimeout(resolve, 500))
            if (checkConnection()) {
              return
            }
          }
        }
      } catch (err) {
        // AppKit 直接連接失敗，使用備用方案
      }

      // 備用方案：使用 Universal Connector（會顯示選擇器）
      if (!connector.value) {
        await initConnector()
      }
      
      if (connector.value) {
        const { session: providerSession } = await connector.value.connect()
        if (providerSession) {
          updateSession(providerSession)
          connectionType.value = 'walletconnect'
        }
      }
    } catch (err: any) {
      error.value = err.message || '連接錢包失敗'
      logger.error('連接錢包失敗:', err)
      throw err
    } finally {
      isConnecting.value = false
    }
  }

  // 連接錢包
  async function connect() {
    try {
      isConnecting.value = true
      error.value = null

      // 確保連接器已初始化
      if (!connector.value) {
        await initConnector()
      }

      if (!connector.value) {
        throw new Error('連接器初始化失敗')
      }

      if (!connector.value.provider) {
        throw new Error('Provider 未初始化')
      }

      // 打開連接模態框（這會顯示 QR 碼）
      // 注意：connect() 返回不代表連接完成
      // 連接完成需要等待用戶掃描 QR 碼，通過事件監聽器處理
      try {
        const result = await connector.value.connect()
        const providerSession = result?.session || null
        
        if (providerSession) {
          // 如果立即有會話（可能是恢復的會話），更新狀態
          updateSession(providerSession)
          connectionType.value = 'walletconnect' // 標記為 WalletConnect 連接
          isConnecting.value = false
        }
        // 如果沒有立即會話，等待用戶掃描 QR 碼
        // session_update 事件會處理連接完成

        return providerSession
      } catch (connectErr: any) {
        // 連接錯誤可能是因為用戶取消或網絡問題
        // 檢查是否是用戶取消
        if (connectErr?.message?.includes('User rejected') || 
            connectErr?.message?.includes('rejected') ||
            connectErr?.code === 4001) {
          throw new Error('用戶取消了連接請求')
        }
        // 其他錯誤
        logger.error('UniversalConnector connect 錯誤:', connectErr)
        throw new Error(connectErr?.message || '連接失敗，請重試')
      }
    } catch (err: any) {
      const errorMessage = err.message || '連接錢包失敗'
      error.value = errorMessage
      logger.error('連接錢包失敗:', err)
      isConnecting.value = false
      throw new Error(errorMessage)
    }
    // 注意：不在 finally 中設置 isConnecting = false
    // 因為連接完成需要等待用戶掃描 QR 碼，由事件監聽器處理
  }

  // 重置連接狀態（用於取消連接或清理）
  function resetConnectingState() {
    isConnecting.value = false
    error.value = null
  }

  // 斷開連接
  async function disconnect() {
    try {
      isConnecting.value = false // 確保重置連接狀態
      
      // 根據連接類型使用不同的斷開方法
      if (connectionType.value === 'walletconnect' && connector.value?.provider) {
        // WalletConnect 連接：需要調用 disconnect
        try {
          await connector.value.provider.disconnect()
        } catch (err) {
          // 如果已經斷開，忽略錯誤
          // WalletConnect 已斷開或無需斷開
        }
      } else if (connectionType.value === 'direct') {
        // 直接連接：只需要清除狀態，不需要調用 disconnect
        // 斷開直接連接
      }
      
      // 清除所有狀態
      session.value = null
      connectionType.value = null
      error.value = null
      isConnecting.value = false // 再次確保重置
    } catch (err: any) {
      // 即使出錯也清除狀態
      session.value = null
      connectionType.value = null
      error.value = err.message || '斷開連接失敗'
      isConnecting.value = false // 確保重置連接狀態
      logger.error('斷開連接失敗:', err)
      // 不 throw，讓 UI 可以正常更新
    }
  }

  // 更新會話
  function updateSession(sessionData: any) {
    if (!sessionData) {
      session.value = null
      return
    }

    try {
      // 解析會話數據
      // sessionData 可能是完整的 session 對象或 namespaces
      const namespaces = sessionData.namespaces || sessionData
      const eip155 = namespaces?.eip155
      
      if (!eip155) {
        session.value = null
        return
      }

      // 獲取賬戶列表
      const accounts = eip155.accounts || []
      // 獲取鏈 ID（從 chains 或 accounts 中提取）
      const chainIdStr = eip155.chains?.[0]?.split(':')[1] || 
                        accounts[0]?.split(':')[1] || 
                        '1'
      
      session.value = {
        accounts: accounts.map((acc: string) => {
          // 格式可能是 "eip155:1:0x..." 或 "0x..."
          const parts = acc.split(':')
          return parts.length > 2 ? parts[2] : parts[parts.length - 1]
        }),
        chainId: parseInt(chainIdStr),
        namespace: 'eip155'
      }
    } catch (err) {
      logger.error('解析會話數據失敗:', err)
      session.value = null
    }
  }

  // 切換網絡
  async function switchChain(chainId: number) {
    try {
      if (!connector.value?.provider) {
        throw new Error('錢包未連接')
      }

      await connector.value.provider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${chainId.toString(16)}` }]
      })
    } catch (err: any) {
      error.value = err.message || '切換網絡失敗'
      logger.error('切換網絡失敗:', err)
      throw err
    }
  }

  // 發送交易
  async function sendTransaction(transaction: {
    to: string
    value?: string
    data?: string
    gasLimit?: string
    gasPrice?: string
  }) {
    try {
      if (!connector.value?.provider) {
        throw new Error('錢包未連接')
      }

      const txHash = await connector.value.provider.request({
        method: 'eth_sendTransaction',
        params: [transaction]
      })

      return txHash
    } catch (err: any) {
      error.value = err.message || '發送交易失敗'
      logger.error('發送交易失敗:', err)
      throw err
    }
  }

  // 簽名消息
  async function signMessage(message: string) {
    try {
      if (!connector.value?.provider || !address.value) {
        throw new Error('錢包未連接')
      }

      const signature = await connector.value.provider.request({
        method: 'personal_sign',
        params: [message, address.value]
      })

      return signature
    } catch (err: any) {
      error.value = err.message || '簽名失敗'
      logger.error('簽名失敗:', err)
      throw err
    }
  }

  // 直接連接（不通過 WalletConnect）
  function setDirectConnection(data: { address: string; chainId: number }) {
    session.value = {
      accounts: [data.address],
      chainId: data.chainId,
      namespace: 'eip155'
    }
    connectionType.value = 'direct' // 標記為直接連接
    error.value = null
    
    // 監聽直接連接的鏈變化（如果使用 window.ethereum）
    if (typeof window !== 'undefined' && window.ethereum && typeof window.ethereum.on === 'function') {
      const ethereum = window.ethereum as any
      
      // 移除舊的監聽器（如果支持）
      if (typeof ethereum.removeAllListeners === 'function') {
        try {
          ethereum.removeAllListeners('chainChanged')
        } catch (err) {
          // 忽略錯誤
        }
      }
      
      // 添加新的鏈變化監聽器
      ethereum.on('chainChanged', async (newChainId: string) => {
        const chainIdNum = parseInt(newChainId, 16)
        // 直接連接鏈變化
        if (session.value) {
          session.value.chainId = chainIdNum
        }
        // 如果切換到非目標鏈，提示用戶
        if (!isTargetChain(chainIdNum)) {
          logger.warn('檢測到切換到非目標鏈')
        }
      })
    }
  }

  // 獲取目標網絡配置（從環境變數）
  function getTargetNetworkConfig() {
    // 從環境配置獲取
    const targetChainId = env.chainId
    const rpcUrl = env.rpcUrl || 'http://localhost:8545'
    
    // 根據 Chain ID 返回對應的網絡配置
    const networkConfigs: Record<number, any> = {
      // Hardhat Local
      1337: {
        chainId: `0x${targetChainId.toString(16)}`,
        chainName: 'Hardhat Local',
        nativeCurrency: {
          name: 'Ether',
          symbol: 'ETH',
          decimals: 18
        },
        rpcUrls: [rpcUrl],
        blockExplorerUrls: []
      },
      // BSC 主網
      56: {
        chainId: `0x${targetChainId.toString(16)}`,
        chainName: 'BNB Smart Chain',
        nativeCurrency: {
          name: 'BNB',
          symbol: 'BNB',
          decimals: 18
        },
        rpcUrls: rpcUrl ? [rpcUrl] : [
          'https://bsc-dataseed1.binance.org',
          'https://bsc-dataseed2.binance.org',
          'https://bsc-dataseed3.binance.org',
          'https://bsc-dataseed4.binance.org'
        ],
        blockExplorerUrls: ['https://bscscan.com']
      },
      // BSC 測試網
      97: {
        chainId: `0x${targetChainId.toString(16)}`,
        chainName: 'BSC Testnet',
        nativeCurrency: {
          name: 'BNB',
          symbol: 'tBNB',
          decimals: 18
        },
        rpcUrls: rpcUrl ? [rpcUrl] : [
          'https://data-seed-prebsc-1-s1.binance.org:8545',
          'https://data-seed-prebsc-2-s1.binance.org:8545'
        ],
        blockExplorerUrls: ['https://testnet.bscscan.com']
      },
      // Sepolia 測試網
      11155111: {
        chainId: `0x${targetChainId.toString(16)}`,
        chainName: 'Sepolia',
        nativeCurrency: {
          name: 'Ether',
          symbol: 'ETH',
          decimals: 18
        },
        rpcUrls: rpcUrl ? [rpcUrl] : [
          'https://sepolia.infura.io/v3/YOUR_INFURA_KEY',
          'https://rpc.sepolia.org'
        ],
        blockExplorerUrls: ['https://sepolia.etherscan.io']
      }
    }
    
    // 如果沒有預定義配置，使用通用配置
    if (!networkConfigs[targetChainId]) {
      return {
        chainId: `0x${targetChainId.toString(16)}`,
        chainName: `Chain ${targetChainId}`,
        nativeCurrency: {
          name: 'Ether',
          symbol: 'ETH',
          decimals: 18
        },
        rpcUrls: rpcUrl ? [rpcUrl] : [],
        blockExplorerUrls: []
      }
    }
    
    return networkConfigs[targetChainId]
  }

  // 獲取目標 Chain ID（從環境配置）
  function getTargetChainId(): number {
    // 使用 env 配置（已解析的配置）
    return env.chainId
  }

  // 檢查是否連接到目標網絡
  function isTargetChain(chainId: number | null): boolean {
    const targetChainId = getTargetChainId()
    return chainId === targetChainId
  }

  // 切換到目標網絡
  async function switchToTargetNetwork() {
    try {
      const currentChainId = chainId.value
      const targetChainId = getTargetChainId()
      
      // 如果已經在目標網絡，不需要切換
      if (isTargetChain(currentChainId)) {
        // 已經連接到目標網絡
        return true
      }

      // 需要切換到目標網絡

      // 獲取 provider
      let provider: any = null
      
      if (connectionType.value === 'direct') {
        // 直接連接：使用 window.ethereum
        if (typeof window !== 'undefined' && window.ethereum) {
          provider = window.ethereum
        }
      } else if (connectionType.value === 'walletconnect' && connector.value?.provider) {
        // WalletConnect 連接
        provider = connector.value.provider
      } else if (appKit.value) {
        // 使用 AppKit 的 provider
        const universalProvider = await appKit.value.getUniversalProvider()
        if (universalProvider) {
          provider = universalProvider
        }
      }

      if (!provider) {
        throw new Error('無法獲取錢包 provider')
      }

      // 獲取目標網絡配置
      const networkConfig = getTargetNetworkConfig()

      try {
        // 嘗試切換到目標網絡（如果錢包已經有這個網絡）
        await provider.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: networkConfig.chainId }]
        })
        // 成功切換到目標網絡
        return true
      } catch (switchError: any) {
        // 如果錯誤代碼是 4902，表示錢包沒有這個網絡，需要添加
        if (switchError.code === 4902 || switchError.message?.includes('does not exist') || switchError.message?.includes('Unrecognized chain')) {
          // 錢包沒有目標網絡，正在添加
          try {
            // 添加目標網絡
            await provider.request({
              method: 'wallet_addEthereumChain',
              params: [networkConfig]
            })
            // 成功添加並切換到目標網絡
            return true
          } catch (addError: any) {
            logger.error('添加目標網絡失敗:', addError)
            throw new Error(`無法添加目標網絡 (Chain ID: ${targetChainId})。請手動在錢包中添加網絡。`)
          }
        } else if (switchError.code === 4001) {
          // 用戶拒絕
          throw new Error('用戶拒絕切換網絡')
        } else {
          // 其他錯誤
          logger.error('切換到目標網絡失敗:', switchError)
          throw new Error(`切換網絡失敗: ${switchError.message || '未知錯誤'}`)
        }
      }
    } catch (err: any) {
      error.value = err.message || '切換到目標網絡失敗'
      logger.error('切換到目標網絡失敗:', err)
      throw err
    }
  }

  // 檢查並確保連接到目標網絡（連接後自動調用）
  async function ensureTargetNetworkConnection() {
    try {
      const currentChainId = chainId.value
      const targetChainId = getTargetChainId()
      
      if (!currentChainId) {
        logger.warn('無法獲取當前鏈 ID，跳過網絡檢查')
        return false
      }

      if (isTargetChain(currentChainId)) {
        // 已經連接到目標網絡
        return true
      }

      // 提示用戶需要切換到目標網絡
      // 檢測到非目標網絡，正在切換
      await switchToTargetNetwork()
      return true
    } catch (err: any) {
      logger.error('確保目標網絡連接失敗:', err)
      // 不拋出錯誤，只記錄，讓用戶可以繼續使用
      return false
    }
  }

  // 保留舊的 BSC 相關函數以保持向後兼容
  const BSC_CHAIN_ID = 56
  function isBSCChain(chainId: number | null): boolean {
    return chainId === BSC_CHAIN_ID
  }
  async function switchToBSC() {
    // 臨時設置目標為 BSC，然後切換
    const originalChainId = (import.meta as any).env?.VITE_CHAIN_ID
    ;(import.meta as any).env.VITE_CHAIN_ID = '56'
    try {
      await switchToTargetNetwork()
    } finally {
      ;(import.meta as any).env.VITE_CHAIN_ID = originalChainId
    }
  }
  async function ensureBSCConnection() {
    return ensureTargetNetworkConnection()
  }

  return {
    // 狀態
    connector,
    appKit,
    session,
    isConnecting,
    error,
    connectionType,
    // 計算屬性
    isConnected,
    address,
    chainId,
    // 方法
    initAppKit,
    initConnector,
    connect,
    connectWalletById,
    disconnect,
    resetConnectingState,
    switchChain,
    sendTransaction,
    signMessage,
    setDirectConnection,
    updateSession,
    isBSCChain,
    switchToBSC,
    ensureBSCConnection,
    // 新的通用網絡切換函數
    getTargetChainId,
    isTargetChain,
    switchToTargetNetwork,
    ensureTargetNetworkConnection
  }
})

